import {compact} from 'lodash';
import React, {FC, ReactNode} from 'react';
import styled from 'styled-components';

import {Trans} from '../../../../../libs/shared/helpers/src/internationalization/react';
import {Handler} from '../../../../../libs/shared/helpers/src/react/reactHelpers';
import {intersperse} from '../../../../../libs/shared/helpers/src/utilities/arrayHelpers';
import {DropdownItem} from '../../../../../libs/shared/ui/src/components/dropdowns/dropdownItem';
import {DropdownItemContent} from '../../../../../libs/shared/ui/src/components/dropdowns/dropdownItemContent';
import {Icon} from '../../../../../libs/shared/ui/src/components/icon/icon';
import {fontSizes} from '../../../../../libs/shared/ui/src/styles/fontStyles';
import {ellipsis} from '../../../../../libs/shared/ui/src/styles/mixins';
import {MeetingRoomModel} from '../../../../models/api/meetingRoomModel';

/*
 * Props.
 */

interface MeetingRoomDropdownItemProps {
  meetingRoom: MeetingRoomModel;
  onClick: Handler;
}

/*
 * Styles.
 */

const StyledIconDiv = styled.div`
  grid-area: left-icon;
  margin-right: 10px;
  color: ${(p) => p.theme.greys.shade70};
  align-items: center;
  display: grid;
`;

const StyledTitleDiv = styled.div`
  ${ellipsis()};
`;

const StyledDetailsDiv = styled.div`
  display: flex;
  align-items: center;
  color: ${(p) => p.theme.greys.shade70};
  font-size: ${fontSizes.verySmall};

  ${ellipsis()};
`;

const StyledCapacityIcon = styled(Icon)`
  margin-right: 4px;
`;

/*
 * Component.
 */

export const MeetingRoomDropdownItem: FC<MeetingRoomDropdownItemProps> = (props) => (
  <DropdownItem onSelect={props.onClick}>
    <StyledIconDiv>
      <Icon name="meetingRoomSmall" />
    </StyledIconDiv>
    {renderMeetingRoomDetails(props.meetingRoom)}
  </DropdownItem>
);

function renderMeetingRoomDetails(meetingRoom: MeetingRoomModel) {
  const roomDetails = compact([
    renderCapacity(meetingRoom),
    meetingRoom.buildingName,
    renderFloorName(meetingRoom),
  ]);

  if (roomDetails.length > 0) {
    return (
      <DropdownItemContent>
        <StyledTitleDiv>{meetingRoom.roomName}</StyledTitleDiv>
        <StyledDetailsDiv>{intersperse<ReactNode>(roomDetails, ' • ')}</StyledDetailsDiv>
      </DropdownItemContent>
    );
  }

  return <DropdownItemContent>{meetingRoom.roomName}</DropdownItemContent>;
}

function renderCapacity(meetingRoom: MeetingRoomModel) {
  if (!meetingRoom.capacity) {
    return null;
  }

  return [
    <span key="room-details-capacity">
      <StyledCapacityIcon name="sharedFilledTiny" />
    </span>,
    meetingRoom.capacity,
  ];
}

function renderFloorName(meetingRoom: MeetingRoomModel) {
  if (!meetingRoom.floorName) {
    return null;
  }

  return [
    <Trans id="meeting-room-dropdown-item-floor" key="room-details-floor-name">
      Floor
    </Trans>,
    ` ${meetingRoom.floorName}`,
  ];
}
